import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Lightning, ArrowRight, ShieldCheck, ArrowClockwise, GoogleLogo } from "@phosphor-icons/react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1649346716613-d92f359d0c2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzg2NTExNTl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verifyOtp, resendOtp, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [otpState, setOtpState] = useState(null);  // { otpRef, emailMasked, expiresIn, delivered }
  const [code, setCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res?.ok) { navigate("/"); return; }
    if (res?.otpRequired) {
      setOtpState(res);
      setResendCooldown(60);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    const res = await verifyOtp(otpState.otpRef, code);
    setSubmitting(false);
    if (res?.ok) navigate("/");
  };

  const onResend = async () => {
    if (resendCooldown > 0) return;
    const res = await resendOtp(otpState.otpRef);
    if (res?.ok) setResendCooldown(60);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const backToPassword = () => {
    setOtpState(null);
    setCode("");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left form */}
      <div className="flex flex-col px-8 py-10 lg:px-20 lg:py-16">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary text-primary-foreground flex items-center justify-center">
            <Lightning size={16} weight="fill" />
          </div>
          <div>
            <div className="font-display font-bold text-base leading-none">LeadFlow</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Ops Console</div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm fade-up">
            <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
              Access · Telesales Operations
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-bold leading-[0.95]">
              Lead status,<br />
              <span className="text-primary">unified.</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
              One console for Google Ads, Meta, WhatsApp, referrals, walk-ins and
              appointments — across nine branches and every telesales agent.
            </p>

            <form onSubmit={otpState ? onVerify : onSubmit} className="mt-10 space-y-5" data-testid="login-form">
              {!otpState ? (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="w-full px-3 py-3 bg-transparent border border-border rounded-sm text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="w-full px-3 py-3 bg-transparent border border-border rounded-sm text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="border border-primary/30 bg-primary/5 px-4 py-3 rounded-sm flex items-start gap-3">
                    <ShieldCheck size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      We sent a 6-digit code to <span className="font-mono font-semibold">{otpState.emailMasked}</span>.
                      {!otpState.delivered && (
                        <div className="mt-2 border border-red-300 bg-red-50 px-2.5 py-2 rounded-sm text-[11px] text-red-800" data-testid="login-otp-delivery-warning">
                          <div className="font-bold uppercase tracking-[0.15em] text-[9px] mb-1">Email not delivered</div>
                          {otpState.deliveryReason === "quota_exceeded" && (
                            <>Your Resend account has hit its monthly quota. Upgrade at <span className="font-mono">resend.com/settings/billing</span> or switch to Gmail SMTP by setting <span className="font-mono">GMAIL_USER</span> + <span className="font-mono">GMAIL_APP_PASSWORD</span> in the backend env.</>
                          )}
                          {otpState.deliveryReason === "rate_limited" && (
                            <>Provider rate-limited the request. Wait 30 seconds and try Resend below.</>
                          )}
                          {otpState.deliveryReason === "auth_failed" && (
                            <>SMTP authentication failed. Double-check <span className="font-mono">GMAIL_APP_PASSWORD</span> — Gmail no longer accepts regular passwords, you need a 16-char App Password.</>
                          )}
                          {otpState.deliveryReason === "recipient_rejected" && (
                            <>The recipient address was rejected by the mail server. Confirm the email is valid.</>
                          )}
                          {otpState.deliveryReason === "unverified_recipient" && (
                            <>Resend's free tier can only send to the account owner. Verify your domain at <span className="font-mono">resend.com/domains</span> and set <span className="font-mono">SENDER_EMAIL</span> to a matching address — OR switch to Gmail SMTP.</>
                          )}
                          {(otpState.deliveryReason === "no_api_key" || otpState.deliveryReason === "no_credentials") && (
                            <>No email provider is configured. Add either Gmail SMTP (<span className="font-mono">GMAIL_USER</span>+<span className="font-mono">GMAIL_APP_PASSWORD</span>) or Resend (<span className="font-mono">RESEND_API_KEY</span>) to the backend env. The OTP was printed to the server logs for dev testing.</>
                          )}
                          {otpState.deliveryReason && otpState.deliveryReason.startsWith("provider_error") && (
                            <>Mail provider rejected the message: <span className="font-mono">{otpState.deliveryReason.replace("provider_error:", "")}</span></>
                          )}
                          {!otpState.deliveryReason && (
                            <>Something went wrong dispatching the email. Check backend logs for details.</>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-2">
                      6-digit code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      autoFocus
                      data-testid="login-otp-input"
                      className="w-full px-3 py-3 bg-transparent border border-border rounded-sm text-center tracking-[0.5em] font-mono text-2xl font-bold focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                      placeholder="000000"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={backToPassword}
                      className="text-muted-foreground hover:text-foreground underline"
                    >← Use a different account</button>
                    <button
                      type="button"
                      onClick={onResend}
                      disabled={resendCooldown > 0}
                      data-testid="login-otp-resend"
                      className="text-primary hover:underline disabled:opacity-40 disabled:no-underline flex items-center gap-1"
                    >
                      <ArrowClockwise size={12} />
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                    </button>
                  </div>
                </>
              )}

              {error && (
                <div
                  className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 rounded-sm"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || (otpState && code.length !== 6)}
                data-testid={otpState ? "login-verify-button" : "login-submit-button"}
                className="group w-full bg-primary text-primary-foreground py-3 px-4 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#002277] transition-colors duration-150 disabled:opacity-60"
              >
                {submitting
                  ? (otpState ? "Verifying…" : "Signing in…")
                  : (otpState ? "Verify & sign in" : "Sign in")}
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </button>

              {!otpState && (
                <>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground pt-2">
                    <div className="flex-1 h-px bg-border" />
                    <span>or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <button
                    type="button"
                    onClick={onGoogleSignIn}
                    data-testid="login-google-btn"
                    className="w-full border border-border py-3 px-4 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-150"
                  >
                    <GoogleLogo size={18} weight="bold" className="text-[#4285F4]" />
                    Sign in with Google
                  </button>
                  <div className="text-[10px] text-muted-foreground text-center">
                    Google sign-in requires your admin to enable it on your account.
                  </div>
                </>
              )}

              <div className="text-[11px] text-muted-foreground border-t border-border pt-4 mt-6 leading-relaxed">
                Contact your administrator if you don&apos;t have access.
              </div>
            </form>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          © {new Date().getFullYear()} LeadFlow · v0.1
        </div>
      </div>

      {/* Right image */}
      <div className="hidden lg:block relative bg-[#0A0A0A] grain overflow-hidden">
        <img
          src={LOGIN_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-[#000814]/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">Now tracking</div>
          <div className="font-display text-4xl tracking-tight font-bold mt-2 leading-none">
            14 sources · 9 branches · 6 agents
          </div>
          <div className="grid grid-cols-3 gap-px mt-8 border border-white/20">
            {["Google Ads", "Meta", "Referrals", "WhatsApp", "Appointments", "Walk-in"].map((s) => (
              <div key={s} className="bg-black/40 px-4 py-3 text-xs uppercase tracking-[0.18em]">
                {s}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
